import { useState } from "react";
import { useStoreState } from "ezStore";
import moment from "moment";
import { intl } from "LocaleWrapper";
import { saveAs } from "file-saver";
import { useQuery } from "@apollo/client";

import { FormGroup, Button } from "@cofano/cck";
import { useCoastguard } from "@cofano/coastguard2-frontend-client";

import { Box, Popover } from "@mui/material";

import ShipSelect, { ShipSelectOption } from "view/ships/components/selects/ShipSelect";

import DateRangePicker from "components/cck/DateRangePicker";
import usePopover from "components/hooks/usePopover";

import { usePermission } from "imports/Utils";

import { GetCompactShipDocument } from "__generated__/view/ships/graphql/ship.queries.generated";

const SailingReportsDownloadPopover = () => {

	const { user } = useStoreState(store => store.session);
	const { isSkipperOrShipManager } = usePermission();
	const { instance } = useCoastguard();

	const { popoverProps, onPopoverOpen } = usePopover({
		trigger: "click",
	});

	const { data } = useQuery(GetCompactShipDocument, {
		skip: !isSkipperOrShipManager || !user?.properties.shipIds?.[0],
		variables: {
			id: user?.properties.shipIds?.[0] ?? 0,
		},
	});

	const [ selectedShip, setSelectedShip ] = useState<ShipSelectOption | null>(null);
	const [ window, setWindow ] = useState<[Date | null, Date | null]>([ null, null ]);
	const [ loading, setLoading ] = useState(false);

	const ship = selectedShip ?? data?.ship;

	const getTimestamp = (date: Date | null) => date ? moment(date).unix() * 1000 : "";

	const onDownload = async() => {

		setLoading(true);

		const url = `/shipreports/generate/old/${ship?.id}?from=${getTimestamp(window[0])}&to=${getTimestamp(window[1])}`;
		instance.fetchBlob(url, {
			method: "GET",
			headers: {
				Accept: "application/vnd.ms-excel",
			},
		}).then(async (blob) => {
			saveAs(blob, "ShipReports.xlsx");
		}).finally(() => {
			setLoading(false);
		});
	};

	return (
		<div>
			<Button variant="outlined" onClick={(event) => onPopoverOpen(event)}>
				{intl.formatMessage({ id: "DOWNLOAD" })}
			</Button>

			<Popover {...popoverProps}>
				<Box p={2} sx={{ width: 300 }}>
					<FormGroup label={intl.formatMessage({ id: "SHIP" })} overflow="hidden">
						<ShipSelect
							value={ship}
							onChange={value => setSelectedShip(value)}
							disabled={isSkipperOrShipManager} />
					</FormGroup>

					<FormGroup label={intl.formatMessage({ id: "START_END_WINDOW" })} overflow="hidden">
						<DateRangePicker
							startDate={window[0] ?? undefined}
							endDate={window[1] ?? undefined}
							onChange={newWindow => setWindow(newWindow)} />
					</FormGroup>

					<div>
						<Button
							onClick={onDownload}
							loading={loading}
							disabled={(!ship || !window[0] || !window[1] || loading)}
							variant="outlined"
							size="small">
							{intl.formatMessage({ id: "DOWNLOAD" })}
						</Button>
					</div>
				</Box>
			</Popover>
		</div>
	);
};

export default SailingReportsDownloadPopover;
